import type { typeProps } from '@components/Form/FormFieldTsoPq';
import FormFieldTsoPq from '@components/Form/FormFieldTsoPq';
import FormTsoPq from '@components/Form/FormTsoPq';
import { history } from '@constants/measurment';
import {
  useDeleteVoltageLevelByIdMutation,
  usePostVoltageLevelMutation,
  usePutVoltageLevelMutation,
} from '@redux/apiSlices/voltageLevelApi';
import { useAppDispatch } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { commentSchema, mandatoryStringPositiveIntegerSchema, mandatoryStringSchema } from '@utils/validationSchema';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { object } from 'zod';
import FormModalWrapper from '../GenericModalWrapper';
import { StyledInputsWrapper } from '../StyledMasterdataModal';
import { RowDataVoltageLevel } from './Voltage';

type voltageLevelProps = {
  newVoltageLevel?: boolean;
  handleDiscard?: any;
  defaultValues?: Record<string, any>;
  selectedVoltageLevel?: RowDataVoltageLevel;
  updateDisabled?: boolean;
  deleteDisabled?: boolean;
};

const FormContent = ({
  register,
  errors,
  updateDeleted,
  defaultValues,
}: {
  register: any;
  errors: any;
  updateDeleted?: boolean;
  defaultValues?: Record<string, any>;
}) => {
  const { t } = useTranslation();
  const fields: {
    name: string;
    label: string;
    required?: boolean;
    multiline?: boolean;
    rows?: number;
    customWidth?: string;
    type?: typeProps;
    disabled?: boolean;
    supportingText?: string;
  }[] = [
    {
      name: 'name',
      label: `${t('masterdata.voltageLevel.table.voltageLevelName')}`,
      required: true,
      type: 'text',
    },
    {
      name: 'valueInKilovolt',
      type: 'integer',
      label: `${t('masterdata.voltageLevel.table.voltageLevelValue')}`,
      required: true,
      disabled: updateDeleted && defaultValues?.valueInKilovolt != null,
      supportingText: updateDeleted ? t('masterdata.generalLimitationHasData') : '',
    },
    {
      name: 'comment',
      label: `${t('masterdata.voltageLevel.table.comment')}`,
      multiline: true,
      rows: 5,
      customWidth: '47.125rem',
      type: 'text',
    },
  ];
  return (
    <StyledInputsWrapper>
      {fields.map(
        ({
          name,
          label,
          required = false,
          multiline = false,
          rows = 1,
          customWidth,
          type = 'text',
          disabled,
          supportingText,
        }) => (
          <FormFieldTsoPq
            key={name}
            name={name}
            label={label}
            type={type}
            errorMessage="general.error.characterLimit"
            register={register}
            required={required}
            multiline={multiline}
            rows={rows}
            customWidth={customWidth}
            placeholder={'masterdata.generalPlaceholder'}
            errors={errors}
            disabled={disabled}
            supportingText={supportingText}
          />
        )
      )}
    </StyledInputsWrapper>
  );
};

const VoltageLevelModal = ({
  newVoltageLevel,
  handleDiscard,
  defaultValues = {},
  selectedVoltageLevel,
  updateDisabled,
  deleteDisabled,
}: voltageLevelProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [addEntity] = usePostVoltageLevelMutation();
  const [editEntity] = usePutVoltageLevelMutation();
  const [deleteVoltageLevelById] = useDeleteVoltageLevelByIdMutation();

  const renderFormContent = useCallback(
    (props: any) => <FormContent {...props} updateDeleted={updateDisabled} defaultValues={defaultValues} />,
    [updateDisabled, defaultValues]
  );

  const validationSchema = object({
    name: mandatoryStringSchema,
    valueInKilovolt: mandatoryStringPositiveIntegerSchema,
    comment: commentSchema,
  });

  const handleDeleteConfirm = async () => {
    if (!selectedVoltageLevel?.id) return;

    try {
      await deleteVoltageLevelById(selectedVoltageLevel.id).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.voltageLevel.delete.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (err: any) {
      const requestID = err?.data?.traceId;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.voltageLevel.delete.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      handleDiscard();
    }
  };

  const handleSubmit = async (data: any) => {
    const payload = newVoltageLevel
      ? { externalDto: { tsoId: 1, ...data } }
      : { externalDto: { ...selectedVoltageLevel, ...data } };

    try {
      if (newVoltageLevel) {
        await addEntity(payload).unwrap();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.voltageLevel.new.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      } else {
        await editEntity(payload).unwrap();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.voltageLevel.update.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      }
    } catch (err: any) {
      const requestID = err?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: newVoltageLevel ? t('snackbar.voltageLevel.new.error') : t('snackbar.voltageLevel.update.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      handleDiscard();
    }
  };

  return (
    <FormModalWrapper
      show={!!(selectedVoltageLevel || newVoltageLevel)}
      newItem={newVoltageLevel}
      title={t('masterdata.voltageLevel.modal.title')}
      titleEdit={t('masterdata.voltageLevel.modal.titleEdit')}
      onSubmit={handleSubmit}
      onDiscard={handleDiscard}
      defaultValues={defaultValues}
      showDeleteButton={!!selectedVoltageLevel}
      formContent={renderFormContent}
      formComponent={FormTsoPq}
      formProps={{ customProps: history, schema: validationSchema }}
      deleteButtonDisabled={deleteDisabled}
      tooltipText={
        deleteDisabled ? t('masterdata.voltageLevel.cannotDelete') : t('masterdata.voltageLevel.deleteTitle')
      }
      deleteTitle={t('masterdata.voltageLevel.deleteTitle')}
      deleteText={t('masterdata.voltageLevel.deleteContent')}
      onDeleteConfirm={handleDeleteConfirm}
    />
  );
};

export default VoltageLevelModal;
